/**
 * editableFields (Inventory Screens Step 5.2 Phase 2) — the D-5.2-E editable
 * manifest for the field record modal.
 *
 * LOCKSTEP MIRROR of the backend capture allowlist FIELD_CAPTURE_BASE_ALLOWED
 * (function_app.py:47-56) + FIELD_CAPTURE_DISPOSAL_EXTRA (:58-60) — the
 * client has no runtime view of the server allowlist, so this list mirrors it
 * the same way data_exchange_manifest mirrors ALLOWED_FIELDS (update BOTH in
 * lockstep when the allowlist changes). The exclusion sets ARE live code:
 * SERVER_OWNED and CHILD_SENTINELS import from buildCapturePayload and filter
 * this manifest at module init — a server-owned key can never ship as an
 * editor even if someone adds it here by mistake.
 *
 * Excluded by construction (never in the allowlist, never here): recon-owned
 * recon_status; server-owned startDate / estimated_tool_size /
 * calculated_weight / material_source.
 *
 * approved_scrap, marked and scrap_project_number JOINED THE ALLOWLIST on
 * 2026-09-04 (Unit F) and are editable here — they were in the excluded list
 * above until then. MARKED is no longer DISPOSAL-gated either; see its entry
 * for why that name means two different fields.
 *
 * kinds: text | number | bool | enum (enum carries refKey — the ReferenceData
 * field name; options are ALWAYS refdata-fed, §4.4).
 *
 * VIEW MEMBERSHIP (INV / ALL): the manifest is one list; the two views are
 * a filter over it, expressed as INV_KEYS below. ALL shows everything, INV
 * shows the INV_KEYS subset. Membership is presentation only — it never
 * narrows what Save sends, because buildCapturePayload iterates the record's
 * `dirty` sentinels, not any field list. Hiding a field cannot drop its
 * value.
 *
 * DISPLAY ORDER is a third, separate decision: FIELD_ORDER below.
 */

import { SERVER_OWNED, CHILD_SENTINELS } from '../sync/buildCapturePayload';

const BASE_FIELDS = [
  { key: 'alt_tool_id', label: 'ALT TOOL ID', kind: 'text' },
  { key: 'fix', label: 'FIX', kind: 'bool' },
  { key: 'found', label: 'FOUND', kind: 'bool' },
  { key: 'added_by_tmg', label: 'ADDED', kind: 'bool' },
  { key: 'tool_type', label: 'TOOL TYPE', kind: 'enum', refKey: 'tool_type' },
  // OEM (OEM arc Phase 2): a FIXED list from refdata-oem-global, never typed.
  // offListCurrent:'readonly' makes BottomSheetSelect apply the fixed-list
  // rules — a stored value not on the list shows read-only in the sheet's
  // header, never as a row, and there is no "— none —" row (blank is not a
  // choice; TBD and OTHER carry those meanings). The other enums keep the
  // default and are unchanged. Backend: in FIELD_CAPTURE_BASE_ALLOWED and
  // uppercased by _normalize_capture_value (Phase 1); not create-stamped, so
  // an OEM picked while creating rides the post-create follow-up capture.
  { key: 'oem', label: 'OEM', kind: 'enum', refKey: 'oem', offListCurrent: 'readonly' },
  { key: 'quantity', label: 'QUANTITY', kind: 'number' },
  { key: 'die_op', label: 'DIE/OP', kind: 'text' },
  { key: 'tool_code', label: 'TOOL CODE', kind: 'text' },
  { key: 'cspc', label: 'CSPC', kind: 'text' },
  { key: 'platform', label: 'PLATFORM', kind: 'text' },
  { key: 'part_description', label: 'PART DESCRIPTION', kind: 'text' },
  { key: 'tool_description', label: 'TOOL DESCRIPTION', kind: 'text' },
  // D-5.2-D: MATERIAL editable; the server latches material_source=MANUAL on
  // a human edit. The provenance chip is display-only.
  { key: 'material', label: 'MATERIAL', kind: 'enum', refKey: 'material', provenance: true },
  { key: 'capacity_flag', label: 'CAPACITY', kind: 'bool' },
  { key: 'npn_flag', label: 'NPN', kind: 'bool' },
  { key: 'site_location', label: 'SITE LOC', kind: 'text' },
  { key: 'detail_location', label: 'SECTION / DETAIL LOCATION', kind: 'text' },
  { key: 'row_nbr', label: 'ROW NBR', kind: 'text' },
  { key: 'stack_nbr', label: 'STACK NBR', kind: 'text' },
  { key: 'rack_nbr', label: 'RACK NBR', kind: 'text' },
  { key: 'shelf_nbr', label: 'SHELF NBR', kind: 'text' },
  { key: 'skid_nbr', label: 'SKID NBR', kind: 'text' },
  { key: 'length', label: 'LENGTH', kind: 'number' },
  { key: 'width', label: 'WIDTH', kind: 'number' },
  { key: 'height', label: 'HEIGHT', kind: 'number' },
  { key: 'estimated_weight', label: 'ESTIMATED WEIGHT', kind: 'number' },
  { key: 'weight_unit', label: 'UNIT', kind: 'enum', refKey: 'weight_unit' },
  { key: 'inventory_comments', label: 'INVENTORY COMMENTS', kind: 'text' },
  // ── Scrap trio (Unit F, 2026-09-04) — Bob's positions 54/55/56 ──────────
  { key: 'approved_scrap', label: 'APPROVED SCRAP', kind: 'bool' },
  // ⚠️ Tools.marked IS A PLAIN BOOLEAN AND IS NOT THE LOCATE/MARKED ENUM.
  // Two different fields share this name. The enum — values ['LOCATE',
  // 'MARKED'], vocabulary refdata-marked-global — belongs to
  // DisposalWorkflow.marked, which get_disposal reads and update_disposal
  // writes (ADMIN-only). Tools.marked is a separate field with NO reader
  // anywhere in the system: get_disposal names 13 tool fields in its merge
  // (function_app.py:627-647) and marked is not among them, and prod
  // ChangeLog holds ZERO edits to it across 7,804 records. Its 169 prod
  // values are 'LOCATE' strings from one Green Tokai bulk import
  // (migrate_greentokai_v1.py:679), left in place deliberately — converting
  // them is a separate post-cutover prod operation.
  // DO NOT convert this to kind:'enum'. DO NOT add it to FIELD_REF_KEYS or
  // wire it to refdata-marked-global — that vocabulary is not this field's.
  { key: 'marked', label: 'MARKED', kind: 'bool' },
  { key: 'scrap_project_number', label: 'SCRAP PROJECT NUMBER', kind: 'text' },
];

// Belt-and-suspenders: server-owned / child-sentinel keys can never become
// editors, no matter what this file says.
export const EDITABLE_FIELDS = BASE_FIELDS.filter(
  (f) => !SERVER_OWNED.has(f.key) && !CHILD_SENTINELS.has(f.key)
);

/**
 * The INV view: the editable keys a rep needs while walking a rack. Listed
 * literally because this is a product decision, not a derivation — it is the
 * one half that has to be written down. `oem` joined in OEM arc Phase 2 (the
 * spec puts the picker in both views). LENGTH, WIDTH, HEIGHT, ESTIMATED
 * WEIGHT and UNIT joined on Bob's INV view request of 2026-10-13
 * (docs/archive/reference/INV_VIEW_CURRENT_vs_PROPOSED.xlsx), so a rep can
 * enter them without switching to ALL. Their read-only neighbour CALCULATED
 * WEIGHT came in through READONLY_INV_KEYS in the same change.
 */
export const INV_KEYS = new Set([
  'alt_tool_id',
  'fix',
  'found',
  'added_by_tmg',
  'tool_type',
  'oem',
  'quantity',
  'die_op',
  'tool_code',
  'cspc',
  'tool_description',
  'capacity_flag',
  'npn_flag',
  'site_location',
  'detail_location',
  'row_nbr',
  'stack_nbr',
  'rack_nbr',
  'shelf_nbr',
  'skid_nbr',
  'length',
  'width',
  'height',
  'estimated_weight',
  'weight_unit',
  'inventory_comments',
]);

/**
 * What ALL adds over INV. DERIVED, never listed — writing both halves by
 * hand is exactly how a new field ends up in neither view, visible in
 * nobody's form and silently unreachable. It is every BASE_FIELDS key that
 * INV_KEYS does not name; read the Set, not a copy of it. A copy used to sit
 * here ("Currently 8", then a list) and went stale twice: the scrap trio
 * joined the Set on 2026-09-04 while the copy still said 8, and the five
 * dimension and weight editors left it for INV on 2026-10-13.
 *
 * ORDER HERE MIRRORS BASE_FIELDS, because that is where the Set is derived
 * from — but it is inert: this is only ever read through .has(). Do not
 * treat the sequence above as a contract.
 *
 * `marked` IS here as of 2026-09-04 (Unit F), along with approved_scrap and
 * scrap_project_number: all three moved into BASE_FIELDS and none is in
 * INV_KEYS, so all three are ALL-only by the same derivation as the rest.
 * `marked` used to be excluded from this set because it lived in
 * DISPOSAL_ONLY_FIELDS, which this Set is not derived from.
 */
export const ALL_ONLY_KEYS = new Set(
  BASE_FIELDS.filter((f) => !INV_KEYS.has(f.key)).map((f) => f.key)
);

/**
 * DISPOSAL-module-only extras — host gates on site.site_modules.
 *
 * EMPTY SINCE 2026-09-04 (Unit F), AND KEPT ON PURPOSE. `marked` was its only
 * member and moved to BASE_FIELDS: Bob wants it on every site, not just
 * disposal ones. The array stays defined and exported because three modules
 * import it into a union — fieldValueRules.js:45 (KIND_BY_KEY),
 * useFieldSortState.js:76 (SORTABLE_KEYS) and FieldRecordModal.jsx — and
 * deleting the export would break all three for a saving of four lines.
 * Spreading an empty array is a no-op, so every one of those unions is
 * unchanged by construction.
 *
 * It is also the seam a future module-gated field lands in. Re-populating it
 * needs no other change: the DISPOSAL gate that consumed it was collapsed in
 * FieldRecordModal (see the note there), so a new member would have to
 * restore that branch too.
 */
export const DISPOSAL_ONLY_FIELDS = [
].filter((f) => !SERVER_OWNED.has(f.key) && !CHILD_SENTINELS.has(f.key));

/** Read-only context rows the modal still displays (never editors). */
export const READONLY_DISPLAY_FIELDS = [
  { key: 'supplier_tool_id', label: 'TOOL ID' },
  { key: 'estimated_tool_size', label: 'ESTIMATED TOOL SIZE (server)' },
  // (LBS), not (server): _calc_calculated_weight multiplies EW x QTY x a unit
  // factor (function_app.py:10449-10463, _WEIGHT_UNIT_FACTORS at :10426), so
  // the stored number is ALWAYS pounds no matter what the record's UNIT says.
  // A rep on a KG site reads UNIT=KG beside a pound figure; the label is the
  // only thing on screen that can say so. `format: 'comma'` is honoured by
  // readonlyRow — see the note there for why it is a manifest prop and not a
  // key branch in the renderer.
  { key: 'calculated_weight', label: 'CALCULATED WEIGHT (LBS)', format: 'comma' },
  // Appended — existing order untouched. READ-ONLY DELIBERATELY, and not a
  // UI preference: PRELIMINARY TOOL STATUS is not in the backend capture
  // allowlist (FIELD_CAPTURE_BASE_ALLOWED / FIELD_CAPTURE_DISPOSAL_EXTRA in
  // function_app.py), so sync_field_capture would drop it into dropped_fields.
  // An editor here would let a rep pick a value, dirty the record, watch Save
  // report success, and lose the edit silently. It IS a real Tools field — in
  // update_tool's wide ALLOWED_FIELDS — and arrives on the client already,
  // because get_field_records returns every non-underscore key and
  // buildBaseline copies every key but etag and the child arrays. Nothing new
  // is fetched; this row only surfaces what is already in `working`. ALL-view
  // only: it is absent from READONLY_INV_KEYS below.
  //
  // PLATFORM WAS HERE AND IS NOW EDITABLE. It moved into BASE_FIELDS (index
  // 10, after part_description) in the same commit that added 'platform' to
  // FIELD_CAPTURE_BASE_ALLOWED — the lockstep this file's header demands. It
  // has no other write path in the zone and no normalization on either route,
  // so the promotion was two executable lines: one manifest entry here and
  // one allowlist key in function_app.py. The rest of this commit is comment
  // corrections in four other files that named platform as read-only.
  //
  // OEM WAS HERE TOO AND IS NOW EDITABLE — as a fixed-list kind:'enum' picker,
  // not a text editor: see its entry in BASE_FIELDS. BASE_FIELDS gives it
  // membership only; FIELD_ORDER, below, puts it after CSPC on screen.
  // OEM arc Phase 2. Phase 1 had already put it in the backend allowlist,
  // uppercased it on the capture path and taken it out of CREATE_STAMPED_KEYS.
  { key: 'preliminary_tool_status', label: 'PRELIMINARY TOOL STATUS' },
];

/**
 * SEARCHABLE_FIELDS (Unit 6 Phase 2) — the `working` scalars a records search
 * matches against. Length is 29: 25 non-bool editable + 4 read-only display
 * rows, and the two sets are disjoint. The total did not move when platform
 * was promoted — it changed contributor, not membership (22+6 and 23+5 were
 * the same 28). It moved to 29 on 2026-09-04 (Unit F), when
 * scrap_project_number joined BASE_FIELDS as text; approved_scrap and marked
 * joined with it as bools and are filtered out below. OEM's promotion to an
 * editable enum (OEM arc Phase 2) was another contributor change: 24+5 became
 * 25+4, still 29.
 *
 * PART NUMBERS AND ASSET TAGS ARE SEARCHED TOO, BUT NOT THROUGH THIS LIST.
 * They are child arrays beside `working`, not keys in it, so matchedKeys
 * (FieldSearchOverlay.jsx) reads them directly. Do not add them here — every
 * entry in this list is read as `working[key]`.
 *
 * THE FILTER IS APPLIED TO BASE_FIELDS ONLY, DELIBERATELY. Spreading it over
 * the union instead would be a no-op TODAY — READONLY_DISPLAY_FIELDS entries
 * carry no `kind` at all, and `undefined !== 'bool'` is true, so all four
 * would survive either way. The reason to scope it is the future: a kind-less
 * editable field added to BASE_FIELDS would pass a union-wide filter silently
 * and become searchable without anyone deciding it should. Narrow predicate,
 * explicit set.
 *
 * Bools are out because a substring search over 'true'/'false' matches on
 * letters a rep never typed as data — "a" would hit every FALSE in the site.
 * The seven excluded: fix, found, added_by_tmg, capacity_flag, npn_flag,
 * approved_scrap, marked.
 *
 * MEDIA is not here and cannot be: it has `key: null`, belongs to no manifest,
 * and its cell is a count off record.media.length, not a `working` value.
 *
 * MATCHING vs DISPLAY are separate on purpose. This list decides what MATCHES;
 * the hidden-column set decides what the result table SHOWS. Hiding a column
 * must never hide its matches, or a rep who tidied their grid would silently
 * stop finding tools by the field they tidied away.
 */
export const SEARCHABLE_FIELDS = [
  ...BASE_FIELDS.filter((f) => f.kind !== 'bool'),
  ...READONLY_DISPLAY_FIELDS,
];

/**
 * Which read-only rows survive the INV filter. A product decision, listed
 * literally for the same reason INV_KEYS is. TOOL ID identifies the record
 * and belongs in both views. CALCULATED WEIGHT sits in INV beside the editors
 * it is computed from (ESTIMATED WEIGHT x QUANTITY x the UNIT factor; see its
 * entry above), so a rep who enters a weight in INV sees the result there.
 *
 * ESTIMATED TOOL SIZE STAYS ALL-ONLY ALTHOUGH ITS INPUTS ARE IN INV. It is
 * built from LENGTH, WIDTH and HEIGHT (_calc_estimated_tool_size in
 * function_app.py, a '20x_x28' string), and all three are INV keys, but Bob's
 * INV list names the dimensions and not the size. "Its inputs are in INV" is
 * not a rule here: adding it to complete a pattern needs that decision first.
 * PRELIMINARY TOOL STATUS is ALL-only for its own reason (see its note above).
 *
 * A separate key set rather than a flag on the rows above, so
 * READONLY_DISPLAY_FIELDS keeps its exact shape and every view decision in
 * this file reads the same way: a Set of keys per view.
 */
export const READONLY_INV_KEYS = new Set(['supplier_tool_id', 'calculated_weight']);

/**
 * FIELD_ORDER — Bob's manifest order (toolsight-inv-v11.14), the ONE place
 * display order is decided (Bob P1 item 2b).
 *
 * ORDER ONLY, NEVER MEMBERSHIP. Which fields exist is still EDITABLE_FIELDS
 * plus READONLY_DISPLAY_FIELDS, and which ones a view shows is still INV_KEYS
 * / READONLY_INV_KEYS. This list only says where each one sits. The two
 * manifests stay separate arrays on purpose; what neither can express is a
 * read-only row's position AMONG the editors, and that is all this adds.
 *
 * MEDIA IS NOT HERE. It is a computed count with no field behind it, so no
 * manifest lists it; it rides after ADDED instead (MEDIA_AFTER_KEY, below).
 *
 * A NEW MANIFEST FIELD MUST BE ADDED HERE TOO. The completeness guard in
 * editableFields.fieldOrder.test.js fails until it is. fieldsForView never
 * drops an unlisted field in the meantime: it sorts it to the end.
 */
export const FIELD_ORDER = [
  'supplier_tool_id', // read-only
  'alt_tool_id',
  'fix',
  'found',
  'added_by_tmg',
  'tool_type',
  'quantity',
  'die_op',
  'tool_code',
  'cspc',
  'oem',
  'platform',
  'part_description',
  'tool_description',
  'material',
  'capacity_flag',
  'npn_flag',
  'site_location',
  'detail_location',
  'row_nbr',
  'stack_nbr',
  'rack_nbr',
  'shelf_nbr',
  'skid_nbr',
  'length',
  'width',
  'height',
  'estimated_tool_size', // read-only
  'estimated_weight',
  'weight_unit',
  'calculated_weight', // read-only
  'inventory_comments',
  'approved_scrap',
  'marked',
  'scrap_project_number',
  'preliminary_tool_status', // read-only
];

/**
 * MEDIA_AFTER_KEY — where MEDIA rides. MEDIA is a computed count (the
 * record's photos) with no field behind it, so no manifest lists it and it is
 * absent from FIELD_ORDER; it sits directly after this key instead. Both the
 * records grid (columnsForView) and the record form in edit mode anchor it
 * here. The form's create mode does not: it pins MEDIA first, under the TOOL
 * ID editor, ahead of ADDED.
 */
export const MEDIA_AFTER_KEY = 'added_by_tmg';

/**
 * CHILD-VALUE COLUMNS (P1-2 piece 1) — PART NUMBER 1-3 and ASSET TAG 1-3, in
 * the ALL view only. Each shows one value from the tool's child array; the
 * last slot also shows "+N" when the tool has more than VISIBLE_CHILD_SLOTS.
 *
 * NOT FIELDS. Like MEDIA, these are display columns with no field behind them,
 * so they enter no manifest, no FIELD_ORDER, and never reach fieldsForView —
 * which keeps them out of the record form and the Sort / Filter / Group
 * pickers. They are spliced in by withChildColumns, the ONE insertion rule,
 * called by both the grid (columnsForView) and the Columns picker
 * (FieldZonePlaceholder), so the two list them in the same order.
 *
 * KEYS ARE pn_1..3 / at_1..3, NEVER 'part_numbers' / 'asset_tags'. Those two
 * strings are CHILD_SENTINELS and the search overlay's child-hit keys; a column
 * keyed with either would start tinting on every part-number or tag match.
 */
export const VISIBLE_CHILD_SLOTS = 3;

const CHILD_COLUMN_GROUPS = [
  { group: 'part_numbers', extraKey: 'extra_part_numbers', prefix: 'pn', label: 'PART NUMBER', after: 'cspc' },
  { group: 'asset_tags', extraKey: 'extra_asset_tags', prefix: 'at', label: 'ASSET TAG', after: 'oem' },
];

const CHILD_COLUMN_RUNS = CHILD_COLUMN_GROUPS.map(({ group, extraKey, prefix, label, after }) => ({
  after,
  columns: Array.from({ length: VISIBLE_CHILD_SLOTS }, (_, slot) => Object.freeze({
    key: `${prefix}_${slot + 1}`,
    label: `${label} ${slot + 1}`,
    kind: 'childValue',
    group,
    extraKey,
    slot,
    last: slot === VISIBLE_CHILD_SLOTS - 1,
  })),
}));

/**
 * withChildColumns — the ONE place the child-value columns join a list.
 *
 * Any view other than 'all' gets `list` back unchanged (the same reference).
 * For 'all', a new array with pn_1..3 directly after the entry keyed 'cspc'
 * and at_1..3 directly after 'oem'. An absent anchor appends that group at the
 * end rather than dropping it — MEDIA's rule. Works on grid columns and
 * picker fields alike: both are matched on `key` alone.
 */
export function withChildColumns(list, view) {
  if (view !== 'all') return list;
  const out = [...list];
  for (const { after, columns } of CHILD_COLUMN_RUNS) {
    const anchor = out.findIndex((entry) => entry.key === after);
    out.splice(anchor === -1 ? out.length : anchor + 1, 0, ...columns);
  }
  return out;
}

const ORDER_RANK = new Map(FIELD_ORDER.map((key, i) => [key, i]));

// An unlisted key ranks after every listed one. Array#sort is stable, so
// unlisted fields keep their manifest order at the end — never dropped.
const rankOf = (f) => (ORDER_RANK.has(f.key) ? ORDER_RANK.get(f.key) : FIELD_ORDER.length);

/**
 * fieldsForView — THE view→field-set mapping, in one place (Bob P1 item 2a).
 *
 * The view's editable fields and read-only rows, in FIELD_ORDER. Membership
 * comes from the manifests and the view sets exactly as before; FIELD_ORDER
 * only sorts the union (Bob P1 item 2b). Until then this returned
 * `[...editable, ...readonly]`, which parked the server-computed rows at the
 * right-hand end of the grid instead of beside the fields they derive from.
 *
 * The records grid, the search overlay, the Columns / Sort / Filter / Group
 * pickers and the record form all read this. In edit mode the form renders
 * this list as one sequence, read-only rows in their manifest position; create
 * mode renders the editors alone.
 *
 * Any view other than 'all' is INV, matching every other `=== 'all'` test in
 * the zone. Pure over module constants, so callers may memoise on the view.
 */
export function fieldsForView(view) {
  const isAll = view === 'all';
  const editable = isAll
    ? EDITABLE_FIELDS
    : EDITABLE_FIELDS.filter((f) => INV_KEYS.has(f.key));
  const readonly = isAll
    ? READONLY_DISPLAY_FIELDS
    : READONLY_DISPLAY_FIELDS.filter((f) => READONLY_INV_KEYS.has(f.key));
  return [...editable, ...readonly].sort((a, b) => rankOf(a) - rankOf(b));
}
